import React, { useState, useEffect } from 'react';
import { AlertTriangle, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

// Production flag - set to true to enable production mode
const IS_PRODUCTION = window.location.search.includes('?is_production=True');
const superpossibleKey = localStorage.getItem('superpossible_key');

interface BuildCommercialStep7Props {
  finalCommercial: string;
  userName: string;
  selectedProduct: any;
  sessionId: string;
  /** A cut already shot from this script, if the user is revisiting the step. */
  existingVideoUrl?: string;
  /** Exact cast the user wrote — locked into the shoot. */
  castBrief?: string;
  onPublish: () => void;
  onVideoReady?: (videoUrl: string) => void;
}

const BuildCommercialStep7: React.FC<BuildCommercialStep7Props> = ({ 
  finalCommercial, 
  userName, 
  selectedProduct,
  sessionId,
  existingVideoUrl = '',
  castBrief = '',
  onPublish,
  onVideoReady
}) => {
  const [videoUrl, setVideoUrl] = useState<string>(existingVideoUrl);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string>('');
  const [playbackFailed, setPlaybackFailed] = useState<boolean>(false);
  const [videoPrompt, setVideoPrompt] = useState<string>('');
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  // Start video generation when component mounts and we have a final commercial
  useEffect(() => {
    if (finalCommercial && !videoUrl && !isGeneratingVideo) {
      generateVideo(false);
    }
  }, [finalCommercial]);

  // Timer effect for video generation progress
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isGeneratingVideo) {
      setElapsedTime(0);
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isGeneratingVideo]);

  // Get progress message based on elapsed time
  const getProgressMessage = () => {
    if (elapsedTime < 20) {
      return "Setting up your shot…";
    } else if (elapsedTime < 45) {
      return "Directing the talent…";
    } else if (elapsedTime < 90) {
      return "Adding the magic…";
    }
    return "Trying a safer take…";
  };

  const friendlyVideoError = (message: string) => {
    const text = String(message || "").trim();
    if (
      /reputational|responsible ai|content_blocked|photorealistic individual|looked like a real person/i.test(
        text
      )
    ) {
      return "Google blocked that take because a still looked like a real person. Shoot it again — we now send the product only, not a face.";
    }
    if (/no playable mp4/i.test(text)) {
      return "The model finished without a playable clip. Shoot it again.";
    }
    if (text.length > 220) {
      return `${text.slice(0, 220)}…`;
    }
    return text || "Failed to generate video. Please try again.";
  };

  const generateVideo = async (retry?: boolean) => {
    setIsGeneratingVideo(true);
    setVideoError("");
    setPlaybackFailed(false);
    if (retry) {
      setVideoUrl("");
    }

    try {
      const response = await fetch(`${API_URL}/api/build-commercial/generate-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          superpossible_key: superpossibleKey,
          commercial_script: finalCommercial,
          product: selectedProduct,
          user_name: userName,
          is_production: IS_PRODUCTION,
          cast_brief: castBrief,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate video');
      }

      const data = await response.json();
      
      if (data.success && data.video_url) {
        setVideoUrl(data.video_url);
        // Store video prompt for development mode
        if (!IS_PRODUCTION && data.video_prompt) {
          setVideoPrompt(data.video_prompt);
        }
        // Notify parent component that video is ready
        if (onVideoReady) {
          onVideoReady(data.video_url);
        }
      } else {
        throw new Error(data.error || 'Video generation failed');
      }
    } catch (error: any) {
      console.error('Video generation error:', error.message);
      setVideoUrl('');
      setVideoError(friendlyVideoError(error.message));
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Get appropriate GIF based on elapsed time
  const getProgressGif = () => {
    if (elapsedTime < 20) {
      return "/video_generating_1.gif";
    } else if (elapsedTime < 45) {
      return "/video_generating_2.gif";
    } else {
      return "/video_generating_3.gif";
    }
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([finalCommercial || 'Your commercial is being generated...'], {
      type: 'text/plain'
    });
    element.href = URL.createObjectURL(file);
    element.download = `${userName}-commercial-script.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownloadVideo = () => {
    if (!videoUrl) return;
    const element = document.createElement('a');
    element.href = videoUrl;
    element.download = `${userName || 'commercial'}-commercial.mp4`;
    element.rel = 'noopener';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleShare = async () => {
    if (navigator.share && videoUrl) {
      try {
        await navigator.share({
          title: 'My Commercial Video',
          text: `Check out my professional commercial video for ${selectedProduct ? selectedProduct.title : 'my product'}!`,
          url: videoUrl
        });
      } catch (err) {
        console.log('Error sharing:', err);
        // Fallback: Copy video URL to clipboard
        navigator.clipboard.writeText(videoUrl);
        toast.success('Video URL copied to clipboard!');
      }
    } else {
      // Fallback: Copy video URL to clipboard
      if (videoUrl) {
        navigator.clipboard.writeText(videoUrl);
        toast.success('Video URL copied to clipboard!');
      }
    }
  };

  const productLabel = selectedProduct ? selectedProduct.title : 'your product';
  const showCut = Boolean(videoUrl && !playbackFailed);
  const showError = Boolean(videoError && !isGeneratingVideo);

  return (
    <div className="ed-enter">
      <StepHeading
        index={7}
        kicker={showCut ? 'Final cut' : showError ? 'Cut short' : 'On set'}
        title={showCut ? 'That’s a wrap.' : showError ? 'The shoot failed.' : 'Rolling…'}
        lede={
          showCut
            ? `Your commercial for ${productLabel} is cut and ready. Watch it, then publish it to the product page.`
            : showError
              ? `Nothing playable came back for ${productLabel}. Shoot it again.`
              : `The model is shooting your commercial for ${productLabel}. It takes about a minute.`
        }
      />

      <div className="ed-block">
        <div className="ed-stage">
          {videoUrl && !playbackFailed ? (
            <video
              key={videoUrl}
              src={videoUrl}
              controls
              playsInline
              preload="auto"
              autoPlay
              onError={() => {
                setPlaybackFailed(true);
                setVideoError('The file we received is not a playable video. Shoot it again.');
              }}
            >
              Your browser does not support the video tag.
            </video>
          ) : isGeneratingVideo ? (
            <div className="ed-stage-state">
              <img src={getProgressGif()} alt="" aria-hidden="true" />
              <h3 className="ed-stage-title">Accelerating your vision</h3>
              <p className="ed-stage-text">{getProgressMessage()}</p>
              <p className="ed-stage-text ed-stage-text--mono">
                {String(Math.floor(elapsedTime / 60)).padStart(2, '0')}:
                {String(elapsedTime % 60).padStart(2, '0')} elapsed
              </p>
            </div>
          ) : videoError ? (
            <div className="ed-stage-state" role="alert">
              <span className="ed-stage-icon" aria-hidden="true">
                <AlertTriangle />
              </span>
              <h3 className="ed-stage-title">The shoot failed</h3>
              <p className="ed-stage-text">{videoError}</p>
              <button
                type="button"
                className="ed-btn"
                onClick={() => generateVideo(true)}
              >
                Shoot it again
              </button>
            </div>
          ) : (
            <div className="ed-stage-state">
              <span className="ed-stage-icon" aria-hidden="true">
                <Play />
              </span>
              <p className="ed-stage-text">Waiting for the script…</p>
            </div>
          )}
        </div>
      </div>

      {videoUrl && !playbackFailed && (
        <div className="ed-block">
          <div className="ed-actions">
            <button type="button" className="ed-btn" onClick={handleShare}>
              Share video
            </button>
            <button type="button" className="ed-btn" onClick={handleDownloadVideo}>
              Download video
            </button>
            <button type="button" className="ed-btn" onClick={handleDownload}>
              Download script
            </button>
          </div>
        </div>
      )}

      {!IS_PRODUCTION && videoPrompt && (
        <div className="ed-block">
          <div className="ed-dev">
            <div className="ed-dev-head">
              <span className="ed-dev-tag">DEV</span>
              <span className="ed-kicker">Prompt sent to the video model</span>
            </div>

            <textarea
              value={videoPrompt}
              readOnly
              aria-label="Video model prompt"
              placeholder="The video prompt will appear here when generated…"
              {...noAutofill}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildCommercialStep7;
